import { aptCategoryNames } from './aptCategories'

// Shared daily-action counts for the procurement dashboards (Sounak, 2026-07-30).
// Two lenses over the SAME request data — no new backend, no migration:
//  - hqActionCounts:  what HQ must move, split by source (At Apartment / Centre)
//  - aptActionCounts: where the At Apartment team's own requests stand
// Every count is derived from the request status chain + line fulfilment_status
// / receipt_status / expected_delivery already captured on each line.

const DONE_FULFILMENT = ['delivered', 'unavailable']
const OPEN_RECEIPTS = ['', null, undefined, 'pending']

export const todayStr = () => {
  // Local date (IST here), not UTC — matches the sheet's todayStr() so an
  // early-morning "overdue" doesn't count yesterday. (see HqDailySheet)
  const d = new Date()
  const off = d.getTimezoneOffset()
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10)
}

const isOverdue = (l, today) =>
  l.expected_delivery &&
  String(l.expected_delivery).slice(0, 10) < today &&
  !DONE_FULFILMENT.includes(l.fulfilment_status)

const linesOf = (r) => r.lines || []

// HQ desk: for one source (a filtered set of requests) count the three things
// HQ acts on. Requests are expected to be status in
// final_approved | in_daily | handed_over (the daily-sheet working set).
const hqCountsFor = (requests, today) => {
  let newIn = 0
  let notSent = 0
  let overdue = 0
  requests.forEach((r) => {
    const lines = linesOf(r)
    if (r.status === 'final_approved') {
      // Just cleared approval, HQ hasn't added it to the daily sheet yet.
      newIn += lines.length
      return
    }
    // On the sheet (in_daily / handed_over).
    lines.forEach((l) => {
      if ((l.fulfilment_status || 'pending') === 'pending') notSent += 1
      if (isOverdue(l, today)) overdue += 1
    })
  })
  return { newIn, notSent, overdue }
}

// Split the HQ working set by where the order originated. Apartment requests
// flow through the At Apartment approval chain (origin 'apartment'); everything
// else — direct HQ entries, centre rows, sheet imports — is 'daily_sheet'.
export const hqActionCounts = (requests) => {
  const today = todayStr()
  const withLines = (requests || []).filter((r) => linesOf(r).length > 0)
  const apartment = withLines.filter((r) => r.origin === 'apartment')
  const centre = withLines.filter((r) => r.origin !== 'apartment')
  return {
    apartment: hqCountsFor(apartment, today),
    centre: hqCountsFor(centre, today)
  }
}

// At Apartment desk: where the team's own requests stand. Expects the full
// apartment request list (origin 'apartment', all statuses).
export const aptActionCounts = (requests) => {
  const today = todayStr()
  const list = requests || []
  let awaitingApproval = 0
  let withHq = 0
  let inTransit = 0
  let confirmReceipt = 0
  let discrepancies = 0
  let overdue = 0

  list.forEach((r) => {
    if (['submitted', 'cluster_approved'].includes(r.status)) {
      awaitingApproval += 1
    }
    const onHq = ['final_approved', 'in_daily', 'handed_over'].includes(r.status)
    linesOf(r).forEach((l) => {
      const ff = l.fulfilment_status || 'pending'
      const rc = l.receipt_status
      if (['not_received', 'discrepancy'].includes(rc)) {
        discrepancies += 1
        return
      }
      if (onHq && ff === 'pending') withHq += 1
      else if (['ordered', 'dispatched'].includes(ff)) {
        inTransit += 1
        if (isOverdue(l, today)) overdue += 1
      } else if (ff === 'delivered' && OPEN_RECEIPTS.includes(rc)) {
        confirmReceipt += 1
      }
    })
  })
  return { awaitingApproval, withHq, inTransit, confirmReceipt, discrepancies, overdue }
}

// Kits below 100% delivered. Delegates to aptKitRows so the header count and
// the bars under it can never disagree — aptKitRows prefers the request list's
// live lines over the overview's raw counts (which include HQ-tombstoned
// lines), and this used to compute its own, different answer.
export const kitsIncomplete = (overviewRows, requests) =>
  aptKitRows(overviewRows, requests).filter((row) => !row.complete).length

// ---------------------------------------------------------------------------
// Rollups for the graphical Dashboard tab (Satya, 2026-08-02). The dashboard
// became a read-only picture of where the orders stand; these are the pure
// functions behind each visual. They read the SAME two payloads the tab
// already fetches — fetchAptRequests({origin:'apartment', lines:'1'}) and
// fetchAptOverview() — and reuse the internals above (DONE_FULFILMENT /
// OPEN_RECEIPTS / isOverdue / linesOf) so the picture can never disagree with
// the counts on the cards it replaced. No new endpoint, no migration.
// ---------------------------------------------------------------------------

// Pipeline order, left to right on the flow strip. Exported so the strip, the
// legend and any future consumer walk the same six buckets in the same order.
export const APT_FLOW_STAGES = [
  'awaitingApproval',
  'withHq',
  'sentToVendor',
  'inTransit',
  'confirmReceipt',
  'flagged'
]

// A request that is out of play contributes nothing: a draft was never sent,
// and a rejection is the requester's problem, not the pipeline's. (Editing a
// rejected request reopens it to draft, so both ends are covered here.)
const inPlay = (r) => !['draft', 'rejected'].includes(r.status)

// HQ tombstones a line by flagging it rather than deleting it, and the API
// returns it either way — counting them would inflate every bucket.
const openLinesOf = (r) => linesOf(r).filter((l) => !l.removed_by_hq)

// Where ONE line sits. First match wins, so the six buckets are mutually
// exclusive; null means the line is closed out (receipt confirmed, marked
// unavailable) or its request is not on anybody's desk. Deliberately stricter
// than aptActionCounts, which counts awaitingApproval per REQUEST and lumps
// ordered + dispatched together — the strip needs one line in exactly one bar.
const flowStageOf = (request, line) => {
  const rc = line.receipt_status
  // Short/wrong/not-received outranks everything: it is stuck wherever it is.
  if (['not_received', 'discrepancy'].includes(rc)) return 'flagged'
  if (rc === 'received') return null
  if (['submitted', 'cluster_approved'].includes(request.status)) {
    return 'awaitingApproval'
  }
  const ff = line.fulfilment_status || 'pending'
  if (ff === 'unavailable') return null
  if (ff === 'delivered') return OPEN_RECEIPTS.includes(rc) ? 'confirmReceipt' : null
  if (ff === 'dispatched') return 'inTransit'
  if (ff === 'ordered') return 'sentToVendor'
  // Cleared approval and on HQ's sheet, but no vendor yet.
  if (['final_approved', 'in_daily', 'handed_over'].includes(request.status)) {
    return 'withHq'
  }
  return null
}

// Whole days from a date (or datetime) to `today`, never negative. Anything
// unparseable reads as "no age", which sorts to the bottom of the chase list.
const daysSince = (dateish, today) => {
  if (!dateish) return null
  const then = Date.parse(`${String(dateish).slice(0, 10)}T00:00:00Z`)
  const now = Date.parse(`${today}T00:00:00Z`)
  if (Number.isNaN(then) || Number.isNaN(now)) return null
  return Math.max(Math.round((now - then) / 86400000), 0)
}

// The flow strip: open LINES by stage, with the taxable value behind each bar
// and (for the two vendor stages) how many are past their expected date.
// Also returns how many requests and apartments those open lines came from,
// so the headline sentence and the bar are computed from one pass and cannot
// drift apart, plus the awaiting-approval line count per apartment so the
// budget meters can talk about lines instead of guessing from money.
export const aptFlowCounts = (requests) => {
  const today = todayStr()
  const stages = {}
  APT_FLOW_STAGES.forEach((key) => {
    stages[key] = { key, count: 0, value: 0, overdue: 0 }
  })
  let total = 0
  let requestCount = 0
  const apartments = new Set()
  const awaitingByApartment = {}

  ;(requests || []).forEach((r) => {
    if (!inPlay(r)) return
    let contributed = 0
    openLinesOf(r).forEach((l) => {
      const key = flowStageOf(r, l)
      if (!key) return
      const stage = stages[key]
      stage.count += 1
      // line_total is the TAXABLE (pre-GST) amount — same basis as the
      // overview's used/pipeline_value, so the two never quote different money.
      stage.value += Number(l.line_total || 0)
      // A dispatched line can be past its promised date just as an ordered one
      // can, and the chase list already raises both as "Past due from vendor" —
      // counting overdue on 'sentToVendor' alone made the legend and the chase
      // list quote two different numbers for the same lines.
      if ((key === 'sentToVendor' || key === 'inTransit') && isOverdue(l, today)) {
        stage.overdue += 1
      }
      if (key === 'awaitingApproval' && r.apartment) {
        awaitingByApartment[r.apartment] = (awaitingByApartment[r.apartment] || 0) + 1
      }
      contributed += 1
    })
    if (contributed) {
      total += contributed
      requestCount += 1
      // apartment_name is OMITTED (not null) on HQ direct rows, hence the fallback.
      apartments.add(r.apartment || r.apartment_name || r.uuid)
    }
  })

  return {
    order: APT_FLOW_STAGES,
    stages,
    total,
    requestCount,
    apartmentCount: apartments.size,
    awaitingByApartment
  }
}

// One meter per apartment: budget, approved spend, awaiting-approval value and
// overspend, summed across the categories the overview returned. `used` adds
// uncategorised_spend exactly the way OverviewTab does, so the Dashboard and
// the Budgets tab quote the same figure for the same apartment.
//
// The three fills share ONE denominator — max(budget, used + pipeline) — so
// they stay inside the track even when the pot is blown. Apartments with no
// budget, no spend and nothing pending are dropped: they are not in play.
export const aptBudgetRows = (overviewRows) => {
  const rows = (overviewRows || [])
    .map((apt) => {
      const cats = apt.categories || []
      const budget = cats.reduce((s, c) => s + Number(c.budget || 0), 0)
      const used =
        cats.reduce((s, c) => s + Number(c.used || 0), 0) +
        Number(apt.uncategorised_spend || 0)
      const pipeline = Number(apt.pipeline_value || 0)
      const over = Math.max(used - budget, 0)
      const scale = Math.max(budget, used + pipeline)
      const share = (v) => (scale > 0 ? (v / scale) * 100 : 0)
      return {
        key: apt.apartment,
        name: apt.name || '',
        cluster: apt.cluster || '',
        goLive: apt.go_live_date || null,
        budget,
        used,
        pipeline,
        over,
        remaining: budget - used,
        widths: {
          used: share(Math.min(used, budget)),
          pipeline: share(pipeline),
          over: share(over)
        },
        // Tightest pot on top. No pot at all but money spent = most urgent of
        // the lot; no pot and no money = nothing to say, sorts last.
        ratio: budget > 0 ? (used + pipeline) / budget : (used > 0 ? Infinity : 0)
      }
    })
    .filter((r) => r.budget || r.used || r.pipeline)
  rows.sort((a, b) =>
    a.ratio === b.ratio ? a.name.localeCompare(b.name) : b.ratio - a.ratio
  )
  return rows
}

// Launch kit readiness, worst first. Percent uses OverviewTab's formula
// (unavailable lines leave the denominator), but every number on one row is
// counted over the SAME set of lines.
//
// The overview's kit integers are Count('lines') with no removed_by_hq filter,
// so they include HQ's tombstones; "not yet ordered" is not in that payload at
// all and has to be joined back from the request list on kit.request. Reading
// the denominator from one base and the pending clause from the other meant a
// kit whose every live line had arrived still showed "8 of 12 delivered", sat
// at 67% and never went green. So when the join resolves, ALL four figures are
// counted off the request's live lines; the overview integers are the fallback
// for a join miss (the kit's request is outside the origin='apartment' page),
// where "not yet ordered" stays `pending: null` rather than a wrong 0.
export const aptKitRows = (overviewRows, requests) => {
  const byRequest = {}
  ;(requests || []).forEach((r) => {
    byRequest[r.uuid] = r
  })
  const rows = []
  ;(overviewRows || []).forEach((apt) => {
    ;(apt.kits || []).forEach((kit) => {
      const req = byRequest[kit.request]
      const live = req ? openLinesOf(req) : null
      const countBy = (status) =>
        live.filter((l) => (l.fulfilment_status || 'pending') === status).length
      const lines = live ? live.length : kit.lines || 0
      const delivered = live ? countBy('delivered') : kit.delivered || 0
      const unavailable = live ? countBy('unavailable') : kit.unavailable || 0
      const denominator = Math.max(lines - unavailable, 1)
      const pct = Math.min(Math.round((delivered / denominator) * 100), 100)
      rows.push({
        key: kit.request,
        apartment: apt.name || '',
        kit: kit.kit || '',
        goLive: apt.go_live_date || null,
        delivered,
        denominator,
        unavailable,
        pending: live ? countBy('pending') : null,
        pct,
        complete: delivered >= denominator
      })
    })
  })
  rows.sort(
    (a, b) =>
      a.pct - b.pct ||
      a.apartment.localeCompare(b.apartment) ||
      a.kit.localeCompare(b.kit)
  )
  return rows
}

// How many rows the chase list shows before it collapses into "+N more".
export const APT_ATTENTION_LIMIT = 8

// The chase list: every open line somebody has to do something about, oldest
// wait first. Four kinds only, decided off the SAME flowStageOf() the strip
// uses — late from the vendor, a discrepancy, a not-received, or a delivery
// waiting on us to confirm. Everything else is moving on its own.
//
// The wait is measured from the date that actually started it: the promised
// date for a late line, the delivery date for one we owe a confirmation on.
// A flagged line falls back delivered_on -> expected_delivery -> handed over,
// and a line with none of those has no age and sinks to the bottom.
export const aptAttentionRows = (requests, limit = APT_ATTENTION_LIMIT) => {
  const today = todayStr()
  const all = []

  ;(requests || []).forEach((r) => {
    if (!inPlay(r)) return
    openLinesOf(r).forEach((l) => {
      const stage = flowStageOf(r, l)
      let kind = null
      let anchor = null
      if (stage === 'flagged') {
        kind = l.receipt_status === 'discrepancy' ? 'discrepancy' : 'not_received'
        anchor = l.delivered_on || l.expected_delivery || r.handed_over_at
      } else if (
        (stage === 'sentToVendor' || stage === 'inTransit') &&
        isOverdue(l, today)
      ) {
        kind = 'late'
        anchor = l.expected_delivery
      } else if (stage === 'confirmReceipt') {
        kind = 'confirm'
        anchor = l.delivered_on
      }
      if (!kind) return
      all.push({
        key: l.uuid,
        kind,
        days: daysSince(anchor, today),
        itemName: l.item_name || '',
        qty: l.qty,
        vendor: l.vendor || '',
        receiptNote: l.receipt_note || '',
        deliveredOn: l.delivered_on || null,
        expectedDelivery: l.expected_delivery || null,
        apartmentName: r.apartment_name || '',
        poNumber: r.po_number || ''
      })
    })
  })

  // Longest wait first; a missing anchor has no age and goes last. Late lines
  // break ties ahead of the rest — the vendor is the one holding us up.
  all.sort((a, b) => {
    const ad = a.days == null ? -1 : a.days
    const bd = b.days == null ? -1 : b.days
    if (ad !== bd) return bd - ad
    const alate = a.kind === 'late' ? 0 : 1
    const blate = b.kind === 'late' ? 0 : 1
    return (
      alate - blate ||
      a.apartmentName.localeCompare(b.apartmentName) ||
      a.itemName.localeCompare(b.itemName)
    )
  })

  return { rows: all.slice(0, limit), more: Math.max(all.length - limit, 0) }
}

// Requests whose spend has cleared the full approval chain — the same four
// statuses the overview's SPEND_STATUSES uses.
const SPEND_STATUSES = ['final_approved', 'in_daily', 'handed_over', 'closed']

// Spend the overview payload cannot see: the backend buckets by the six budget
// heads plus '' (uncategorised) and DROPS everything else on the floor, but a
// line on an apartment request can legally carry a daily-sheet category —
// HqDailySheet offers the At Apartment list ('5-12 Art', '5-12 Robotics', …) on any
// line whose request has an apartment, and none of those are budget heads. Left
// out, that money vanished from a chart whose header called itself a total.
//
// Counted over ALL lines (tombstones included) because the overview's own
// category sums are unfiltered too — one basis per chart. Capped by the request
// list the tab already holds (the API returns at most 500 requests), so this is
// a floor on off-head spend, not a guarantee.
const offHeadSpend = (requests) => {
  const budgetHeads = new Set(aptCategoryNames())
  let total = 0
  ;(requests || []).forEach((r) => {
    if (!SPEND_STATUSES.includes(r.status)) return
    linesOf(r).forEach((l) => {
      const category = l.category || ''
      if (!category || budgetHeads.has(category)) return
      total += Number(l.line_total || 0)
    })
  })
  return total
}

// Approved spend by category, biggest first. Bar widths are normalised to the
// LARGEST category (top bar always full); the tooltip percentage is a share of
// the TOTAL — two different denominators on purpose.
//
// The overview only rolls the six budget heads, so a line raised with a blank
// category lands in uncategorised_spend and would otherwise vanish from a
// chart whose header claims a total. It gets its own bar instead, and spend
// booked to a category outside the six heads gets one too (see offHeadSpend),
// which keeps the bars summing to the header figure.
export const aptCategorySpend = (overviewRows, requests) => {
  const byCategory = {}
  let uncategorised = 0
  ;(overviewRows || []).forEach((apt) => {
    ;(apt.categories || []).forEach((c) => {
      const name = c.category || ''
      if (!name) return
      byCategory[name] = (byCategory[name] || 0) + Number(c.used || 0)
    })
    uncategorised += Number(apt.uncategorised_spend || 0)
  })

  const rows = Object.keys(byCategory).map((category) => ({
    category,
    used: byCategory[category]
  }))
  if (uncategorised > 0) rows.push({ category: 'Uncategorised', used: uncategorised })
  const offHead = offHeadSpend(requests)
  if (offHead > 0) rows.push({ category: 'Other categories', used: offHead })

  const ranked = rows.filter((r) => r.used > 0)
  ranked.sort((a, b) => b.used - a.used || a.category.localeCompare(b.category))
  const total = ranked.reduce((s, r) => s + r.used, 0)
  const max = ranked.length ? ranked[0].used : 0

  return {
    total,
    rows: ranked.map((r) => ({
      ...r,
      width: max > 0 ? (r.used / max) * 100 : 0,
      share: total > 0 ? Math.round((r.used / total) * 100) : 0
    }))
  }
}
